import { loadAuthState, saveAuthState } from './auth-store';

const configuredApiBase = (import.meta.env.VITE_API_BASE_URL || '').trim();
const API_BASE_URL = configuredApiBase || (import.meta.env.DEV ? 'http://localhost:4000' : '');

export class ApiError extends Error {
  status: number;
  payload: unknown;

  constructor(message: string, status: number, payload: unknown) {
    super(message);
    this.status = status;
    this.payload = payload;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

function getHeaders(
  extra?: HeadersInit,
  options?: {
    hasBody?: boolean;
  },
): Headers {
  const state = loadAuthState();
  const headers = new Headers(extra || {});

  if (options?.hasBody) {
    headers.set('Content-Type', 'application/json');
  } else {
    headers.delete('Content-Type');
  }

  if (state.accessToken) {
    headers.set('Authorization', `Bearer ${state.accessToken}`);
  }

  if (state.activeTenantId) {
    headers.set('x-tenant-id', state.activeTenantId);
  }

  return headers;
}

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const hasBody = init?.body !== undefined && init?.body !== null;
  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...init,
    headers: getHeaders(init?.headers, { hasBody }),
    credentials: 'include',
  });

  if (response.status === 204) {
    return undefined as T;
  }

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text) as unknown;
    } catch {
      payload = text;
    }
  }

  if (!response.ok) {
    if (response.status === 401 && !path.startsWith('/v1/auth/')) {
      saveAuthState({
        accessToken: null,
        user: null,
        activeTenantId: null,
      });
    }

    const errorMessage = (() => {
      if (typeof payload === 'object' && payload !== null && 'error' in payload) {
        return String((payload as Record<string, unknown>).error);
      }

      if (typeof payload === 'string' && payload.trim().length > 0) {
        return payload;
      }

      return `API error (${response.status})`;
    })();

    throw new ApiError(errorMessage, response.status, payload);
  }

  return payload as T;
}

export async function login(email: string, password: string): Promise<{
  accessToken: string;
  user: any;
}> {
  const payload = await apiRequest<{ accessToken: string; user: any }>('/v1/auth/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });

  saveAuthState({
    accessToken: payload.accessToken,
    user: payload.user,
    activeTenantId: payload.user.memberships?.[0]?.tenantId || null,
  });

  return payload;
}

export async function refreshSession(): Promise<void> {
  const current = loadAuthState();

  if (!current.accessToken) {
    return;
  }

  try {
    const refreshed = await apiRequest<{ accessToken: string; user: any }>('/v1/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({}),
    });

    saveAuthState({
      accessToken: refreshed.accessToken,
      user: refreshed.user,
      activeTenantId: current.activeTenantId || refreshed.user.memberships?.[0]?.tenantId || null,
    });
  } catch {
    saveAuthState({
      accessToken: null,
      user: null,
      activeTenantId: null,
    });
  }
}

export async function logout(): Promise<void> {
  await apiRequest('/v1/auth/logout', {
    method: 'POST',
    body: JSON.stringify({}),
  });

  saveAuthState({
    accessToken: null,
    user: null,
    activeTenantId: null,
  });
}

export function setActiveTenant(tenantId: string | null): void {
  const current = loadAuthState();
  saveAuthState({
    ...current,
    activeTenantId: tenantId,
  });
}

export function getApiBaseUrl(): string {
  return API_BASE_URL;
}
